import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ApiError, api, unwrap } from '../api/client.js';
import { landingUrl } from '../components/Layouts.js';
import { Loading } from '../components/ui.js';
import { formatDate, formatDateTime, formatDuration, formatNumber } from '../i18n.js';

/** Minutes of outage from which a day counts as a real outage (red) rather than a short one. */
const LONG_OUTAGE_SECONDS = 30 * 60;

const BANNER = {
  up: 'bg-emerald-700 text-white',
  degraded: 'bg-amber-500 text-slate-950',
  down: 'bg-red-700 text-white',
  paused: 'bg-slate-600 text-white',
} as const;

const dayColor = (down: number | null) =>
  down === null
    ? 'bg-slate-200'
    : down === 0
      ? 'bg-emerald-600'
      : down < LONG_OUTAGE_SECONDS
        ? 'bg-amber-400'
        : 'bg-red-600';

/**
 * Public status page of a site (T-70), for its visitors: no sign-in, the account's language,
 * nothing technical. Refreshes every minute.
 */
export function StatusPage() {
  const { slug = '' } = useParams();
  const { i18n } = useTranslation();
  const page = useQuery({
    queryKey: ['public-status', slug],
    queryFn: () => unwrap(api.GET('/public/status/{slug}', { params: { path: { slug } } })),
    refetchInterval: 60_000,
    retry: false,
  });
  const locale = page.data?.locale ?? i18n.language;
  const t = i18n.getFixedT(locale);
  useEffect(() => {
    if (page.data) document.title = t('publicStatus.docTitle', { name: page.data.name });
  }, [page.data, t]);

  const home = landingUrl();
  const footer = (
    <footer className="mt-10 text-center text-sm text-slate-600">
      {home ? (
        <a href={`${home}?ref=status`} className="underline hover:text-slate-900">
          {t('publicStatus.poweredBy', { brand: t('app.name') })}
        </a>
      ) : (
        t('publicStatus.poweredBy', { brand: t('app.name') })
      )}
    </footer>
  );

  if (page.isPending)
    return (
      <main className="mx-auto max-w-3xl px-4 py-10">
        <Loading />
      </main>
    );
  if (page.isError || !page.data)
    return (
      <main className="mx-auto max-w-3xl px-4 py-16 text-center">
        <h1 className="text-2xl font-semibold">
          {page.error instanceof ApiError && page.error.code === 'NOT_FOUND'
            ? t('publicStatus.notFound')
            : t('publicStatus.unavailable')}
        </h1>
        {footer}
      </main>
    );

  const p = page.data;
  const tz = p.timezone;
  const day = (d: string) => formatDate(`${d}T12:00:00Z`, locale, 'UTC');
  const dayTitle = (d: { day: string; downSeconds: number | null }) =>
    d.downSeconds === null
      ? t('publicStatus.dayNone', { day: day(d.day) })
      : d.downSeconds === 0
        ? t('publicStatus.dayUp', { day: day(d.day) })
        : t('publicStatus.dayDown', {
            day: day(d.day),
            duration: formatDuration(d.downSeconds, locale),
          });

  return (
    <div className="min-h-screen bg-slate-50" lang={locale}>
      <main className="mx-auto max-w-3xl px-4 py-8 sm:py-12">
        <header className="mb-6 flex flex-wrap items-center gap-4">
          {p.logo ? <img src={p.logo} alt="" className="max-h-14 max-w-48 object-contain" /> : null}
          <div>
            <h1 className="text-2xl font-semibold text-slate-950 sm:text-3xl">{p.name}</h1>
            <p className="text-slate-700">{t('publicStatus.subtitle')}</p>
          </div>
        </header>

        <section
          className={`rounded-xl px-5 py-4 text-lg font-semibold shadow-sm ${BANNER[p.status]}`}
          aria-live="polite"
        >
          {p.status === 'down' && p.since
            ? t('publicStatus.state.downSince', { time: formatDateTime(p.since, locale, tz) })
            : t(`publicStatus.state.${p.status}`)}
        </section>

        {p.message ? (
          <section className="mt-4 rounded-xl border border-blue-200 bg-blue-50 px-5 py-4">
            <h2 className="font-semibold text-blue-950">{t('publicStatus.message')}</h2>
            <p className="mt-1 whitespace-pre-line text-slate-900">{p.message}</p>
            {p.messageAt ? (
              <p className="mt-2 text-sm text-slate-700">
                {formatDateTime(p.messageAt, locale, tz)}
              </p>
            ) : null}
          </section>
        ) : null}

        <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-semibold">
              {t('publicStatus.days', { count: p.days.length })}
            </h2>
            {p.uptimePct !== null ? (
              <p className="tabular-nums text-slate-800">
                {t('publicStatus.uptime', { pct: formatNumber(p.uptimePct, locale, 2) })}
              </p>
            ) : null}
          </div>
          <div
            role="img"
            aria-label={
              p.uptimePct === null
                ? t('publicStatus.days', { count: p.days.length })
                : t('publicStatus.uptime', { pct: formatNumber(p.uptimePct, locale, 2) })
            }
            className="mt-4 flex h-10 items-stretch gap-px"
          >
            {p.days.map((d) => (
              <span
                key={d.day}
                title={dayTitle(d)}
                className={`flex-1 rounded-sm ${dayColor(d.downSeconds)}`}
              />
            ))}
          </div>
          <div className="mt-2 flex justify-between text-xs text-slate-600">
            <span>{p.days[0] ? day(p.days[0].day) : ''}</span>
            <span>{t('publicStatus.today')}</span>
          </div>
          <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-700">
            <li className="flex items-center gap-1">
              <span className="inline-block h-3 w-3 rounded-sm bg-emerald-600" />{' '}
              {t('publicStatus.legendUp')}
            </li>
            <li className="flex items-center gap-1">
              <span className="inline-block h-3 w-3 rounded-sm bg-amber-400" />{' '}
              {t('publicStatus.legendShort')}
            </li>
            <li className="flex items-center gap-1">
              <span className="inline-block h-3 w-3 rounded-sm bg-red-600" />{' '}
              {t('publicStatus.legendDown')}
            </li>
          </ul>
        </section>

        <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold">{t('publicStatus.incidents')}</h2>
          {p.incidents.length === 0 ? (
            <p className="mt-2 text-slate-700">{t('publicStatus.noIncidents')}</p>
          ) : (
            <ul className="mt-3 flex flex-col divide-y divide-slate-100">
              {p.incidents.map((i) => (
                <li key={i.startedAt} className="flex flex-wrap justify-between gap-2 py-2">
                  <span>{formatDateTime(i.startedAt, locale, tz)}</span>
                  <span className="text-slate-700">
                    {i.resolvedAt === null
                      ? t('publicStatus.ongoing')
                      : t('publicStatus.lasted', {
                          duration: formatDuration(i.durationSeconds ?? 0, locale),
                        })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <p className="mt-4 text-sm text-slate-600">
          {t('publicStatus.checkedAt', { time: formatDateTime(p.checkedAt, locale, tz) })}
        </p>
        {footer}
      </main>
    </div>
  );
}
